/**
 * Query keys centralizadas para TanStack Query.
 *
 * Convenções:
 *  - Tenant-escopadas: incluem `tenantId` na key, são invalidadas ao trocar de tenant.
 *  - Globais (me, workers): não incluem `tenantId`, sobrevivem à troca de tenant.
 *  - Admin (modo basic): globais por natureza — operam sobre o conjunto de
 *    organizações/tenants/etc. do sistema.
 *
 * Todas as funções retornam tuplas com `as const` para que o TanStack Query
 * possa inferir o tipo exato da query key (útil em hooks tipados e em
 * `invalidateQueries({ queryKey: ... })`).
 *
 * Referência: spec §8 "State Management" → "TanStack Query".
 */
export const queryKeys = {
  // ──────────────────────────────────────────────────────────────────────
  // Tenant-escopados (invalidados ao trocar tenant via TenantSwitcher)
  // ──────────────────────────────────────────────────────────────────────
  dashboardSummary: (tenantId: string) => ['dashboardSummary', tenantId] as const,
  observabilityAll: (tenantId: string) =>
    ['observability', tenantId] as const,
  observability: (tenantId: string, period: number | string, channel: string) =>
    ['observability', tenantId, period, channel] as const,
  executionEvaluations: (tenantId: string, executionId: string) =>
    ['executionEvaluations', tenantId, executionId] as const,

  testCases: (tenantId: string) => ['testCases', tenantId] as const,
  testCaseSearch: (tenantId: string, search: string, page: number) =>
    ['testCases', tenantId, 'search', search, page] as const,
  testCase: (id: string) => ['testCase', id] as const,
  testCaseVersions: (id: string) => ['testCaseVersions', id] as const,
  testCaseVersion: (tenantId: string, id: string, version: number) =>
    ['testCaseVersion', tenantId, id, version] as const,

  testSuites: (tenantId: string) => ['testSuites', tenantId] as const,
  testSuite: (id: string) => ['testSuite', id] as const,
  testSuiteVersions: (id: string) => ['testSuiteVersions', id] as const,
  testSuiteVersion: (tenantId: string, id: string, version: number) =>
    ['testSuiteVersion', tenantId, id, version] as const,
  suiteRuns: (tenantId: string) => ['suiteRuns', tenantId] as const,
  suiteRun: (id: string) => ['suiteRun', id] as const,
  suiteRunReport: (id: string) => ['suiteRunReport', id] as const,

  datasets: (tenantId: string) => ['datasets', tenantId] as const,
  dataset: (id: string) => ['dataset', id] as const,
  datasetVersions: (id: string) => ['datasetVersions', id] as const,
  datasetMatch: (id: string) => ['datasetMatch', id] as const,
  datasetImportMatch: (tenantId: string, fileId: string, sheet: string, suiteId: string) =>
    ['datasetImportMatch', tenantId, fileId, sheet, suiteId] as const,
  testCasePicker: (tenantId: string, search: string, suiteId: string) =>
    ['testCases', tenantId, 'picker', search, suiteId] as const,
  testSuitePicker: (tenantId: string, search: string) =>
    ['testSuites', tenantId, 'picker', search] as const,

  functions: (tenantId: string) => ['functions', tenantId] as const,
  functionDetail: (id: string) => ['function', id] as const,

  appProfiles: (tenantId: string) => ['appProfiles', tenantId] as const,
  appProfileDetail: (id: string) => ['appProfile', id] as const,
  journeyProfiles: (tenantId: string) => ['journeyProfiles', tenantId] as const,
  journeyProfileDetail: (id: string) => ['journeyProfile', id] as const,

  schedules: (tenantId: string) => ['schedules', tenantId] as const,

  executions: (tenantId: string) => ['executions', tenantId] as const,
  execution: (id: string) => ['execution', id] as const,
  executionReport: (id: string) => ['executionReport', id] as const,

  billing: (tenantId: string, year: number, month: number) =>
    ['billing', tenantId, year, month] as const,

  // Devices (gestão de frota — item 10): tenant-escopada porque tags/notas
  // variam por tenant (o inventário vivo em si é global).
  devices: (tenantId: string) => ['devices', tenantId] as const,

  users: (tenantId: string) => ['users', tenantId] as const,
  profiles: (tenantId: string) => ['profiles', tenantId] as const,
  apiKeys: (tenantId: string) => ['apiKeys', tenantId] as const,
  apiKeyAssignableProfiles: (tenantId: string) =>
    ['apiKeys', tenantId, 'assignableProfiles'] as const,
  voiceSamples: (tenantId: string) => ['voiceSamples', tenantId] as const,

  // ──────────────────────────────────────────────────────────────────────
  // Globais (NÃO invalidados ao trocar tenant)
  // ──────────────────────────────────────────────────────────────────────
  me: () => ['me'] as const,
  workers: () => ['workers'] as const,
  workerProviders: (workerId: string) => ['workerProviders', workerId] as const,
  agentUi: (workerId: string, serial: string) =>
    ['agentUi', workerId, serial] as const,

  // ──────────────────────────────────────────────────────────────────────
  // Imports (parsing de cenários via IA)
  // ──────────────────────────────────────────────────────────────────────
  importParse: (fileId: string) => ['importParse', fileId] as const,

  // ──────────────────────────────────────────────────────────────────────
  // Curadoria de base de conhecimento (tenant-escopada)
  // ──────────────────────────────────────────────────────────────────────
  curationCorpora: () => ['curationCorpora'] as const,
  curationDocuments: (corpusId: string, filtro?: string) =>
    ['curationDocuments', corpusId, filtro ?? 'todos'] as const,
  curationDocument: (documentId: string) => ['curationDocument', documentId] as const,
  curationQueue: (corpusId: string, filtro: string) =>
    ['curationQueue', corpusId, filtro] as const,
  curationQueueStats: (corpusId: string) => ['curationQueueStats', corpusId] as const,
  curationSamples: (corpusId: string) => ['curationSamples', corpusId] as const,
  curationSampleProgress: (sampleId: string) =>
    ['curationSampleProgress', sampleId] as const,
  curationDocumentContent: (documentId: string, version?: number) =>
    version
      ? (['curationDocumentContent', documentId, version] as const)
      : (['curationDocumentContent', documentId] as const),
  curationEstrutura: (documentId: string) =>
    ['curationEstrutura', documentId] as const,
  curationLedger: (documentId: string) => ['curationLedger', documentId] as const,
  curationApprovalQueue: (corpusId: string) => ['curationApprovalQueue', corpusId] as const,
  curationReview: (documentId: string) => ['curationReview', documentId] as const,
  curationProjections: (documentId: string) => ['curationProjections', documentId] as const,
  curationIssues: (corpusId: string) => ['curationIssues', corpusId] as const,
  curationMetadata: (documentId: string) => ['curationMetadata', documentId] as const,
  curationPackages: (corpusId: string) => ['curationPackages', corpusId] as const,
  curationLinks: (documentId: string) => ['curationLinks', documentId] as const,
  curationDashboard: (corpusId: string) => ['curationDashboard', corpusId] as const,
  curationMergeProposal: (issueId: string) => ['curationMergeProposal', issueId] as const,
  curationCorpusRun: (runId: string) => ['curationCorpusRun', runId] as const,
  curationGoldenQuestions: (corpusId: string) => ['curationGoldenQuestions', corpusId] as const,
  curationGoldenRun: (corpusId: string, runId: string | null) =>
    ['curationGoldenRun', corpusId, runId] as const,
  curationRun: (runId: string) => ['curationRun', runId] as const,

  // ──────────────────────────────────────────────────────────────────────
  // Admin global (modo HTTP Basic — admin do sistema)
  // ──────────────────────────────────────────────────────────────────────
  adminOrgs: () => ['adminOrgs'] as const,
  adminTenants: () => ['adminTenants'] as const,
  adminWorkers: () => ['adminWorkers'] as const,
  adminUsers: () => ['adminUsers'] as const,
  adminBilling: () => ['adminBilling'] as const,
  adminAccessEvents: () => ['adminAccessEvents'] as const,
  adminProfiles: (tenantId: string) => ['adminProfiles', tenantId] as const,
  adminLlmDefaults: () => ['adminLlmDefaults'] as const,
  adminLlmRevisions: () => ['adminLlmRevisions'] as const,
} as const;

/**
 * Tipo utilitário: extrai todas as variações de query key produzidas por
 * `queryKeys`. Útil para tipar funções helper que recebem uma key.
 */
export type QueryKey = ReturnType<(typeof queryKeys)[keyof typeof queryKeys]>;
